import * as React from "react";
import { ChatMessage } from "./types";
import classNames from "classnames";
import {
  ProfileImage,
  DEFAULT_PROFILE_IMAGE_HEIGHT,
  DEFAULT_PROFILE_IMAGE_WIDTH,
} from "../ProfileImage";
import { TextWithEmoji } from "../TextWithEmoji";
import { useIntersection } from "use-intersection";
import { useRelativeTimeFormatter } from "../../hooks/useRelativeTimeFormatter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLanguage,
  faExclamationCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { useMessageTranslation } from "../TranslationProvider";
import { GAME_IDS } from "../Games";
import { useMemo } from "react";
import { useCustomChatContext, useUserId } from "../UserProvider";
import { useMutation } from "../../hooks/useMutation";
import {
  ReportChatMessageMutation,
  ReportChatMessageMutationVariables,
} from "../../graphql/ReportChatMessageMutation.generated";
import { loader } from "graphql.macro";

const REPORT_MESSAGE_MUTATION = loader(
  "../../graphql/ReportChatMessageMutation.graphql"
);

const GroupMessage = React.memo<{
  message: string;
  reverse: boolean;
  isLast: boolean;
  id: string;
  currentChatId: string;
  isReported: boolean;
  setIsReported: (messageId: string) => void;
}>(
  ({
    message: original,
    reverse,
    isLast,
    id,
    currentChatId,
    isReported,
    setIsReported,
  }) => {
    const [message, translate] = useMessageTranslation(id, original);
    const [isHovered, setIsHovered] = React.useState(false);
    const { setReportedMessage } = useCustomChatContext();

    const isGiftMessage = useMemo(() => {
      return message.data.includes("virtual-items");
    }, [message]);

    const [, reportChatMessage] = useMutation<
      ReportChatMessageMutation,
      ReportChatMessageMutationVariables
    >(REPORT_MESSAGE_MUTATION, false);

    const reportMessage = () => {
      reportChatMessage({ chatId: currentChatId, messageId: id }).then(
        (data) => {
          if (data?.data?.reportChatMessage) {
            setReportedMessage(true);
            setIsReported(data.data?.reportChatMessage?.id);

            setTimeout(() => {
              setReportedMessage(false);
            }, 3000);
          }
        }
      );
    };

    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={classNames("flex", "-mx-1", {
          "justify-end": reverse,
          "mb-1": !isLast,
        })}
      >
        <div className="px-1">
          <div
            className={classNames(
              "rounded",
              "px-2",
              "py-1",
              "text-sm",
              "break-words",
              "overflow-hidden",
              reverse ? "bg-blue-200" : "bg-gray-300"
            )}
          >
            {isGiftMessage ? (
              <img width={60} height={60} src={message.data} alt="gift" />
            ) : (
              <TextWithEmoji
                text={message.showTranslated ? message.data : message.original}
                className="whitespace-pre-wrap"
                style={{ overflowWrap: "anywhere" }}
              />
            )}
          </div>
        </div>
        {!reverse && isHovered && (
          <div className="flex items-center gap-2 px-1 text-gray-900">
            <button disabled={message.fetching} onClick={translate}>
              {message.fetching ? (
                <span className="relative flex w-3 h-3">
                  <span className="absolute inline-flex w-full h-full bg-blue-500 rounded-full opacity-75 animate-ping" />
                  <span className="relative inline-flex w-3 h-3 bg-blue-600 rounded-full" />
                </span>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faLanguage}
                    className={
                      !message.showTranslated
                        ? "text-gray-500"
                        : "text-blue-500"
                    }
                  />
                </>
              )}
            </button>
            <button disabled={isReported} onClick={reportMessage}>
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className={classNames(
                  "transition-colors",
                  isReported
                    ? "text-red-400 opacity-50 cursor-default"
                    : "text-gray-400 hover:text-orange-500 cursor-pointer"
                )}
                title={isReported ? "Message has been reported" : "Report"}
              />
            </button>
          </div>
        )}
      </div>
    );
  }
);

const Group = ({
  container,
  messages,
  onProfileClick,
  currentChatId,
  setIsReported,
}: {
  container: React.MutableRefObject<HTMLDivElement | null>;
  onProfileClick: (profile: {
    id: string;
    name: string;
    games: string[];
    tournament: boolean;
  }) => void;
  currentChatId: string;
  messages: Array<ChatMessage>;
  setIsReported: (messageId: string) => void;
}) => {
  const id = useUserId();
  const player = messages[0].player;
  const reverse = player.id === id;
  const target = React.useRef<HTMLDivElement>(null);
  const intersecting = useIntersection(target, {
    root: container,
    once: true,
    defaultIntersecting: false,
  });

  const relativeTimeFormatter = useRelativeTimeFormatter();

  const SHORT_NAME_MAX_LENGTH = 12;
  function calculateShortName(name: string) {
    if (!name || typeof name !== "string") {
      return "";
    }

    const split = name.trim().split(/\s+/);

    let firstName = split[0] || "";

    if (firstName.length > SHORT_NAME_MAX_LENGTH) {
      firstName = firstName.substring(0, SHORT_NAME_MAX_LENGTH);
    }

    return firstName;
  }

  return (
    <div className="flex-shrink-0 px-2 mb-2 overflow-hidden">
      <div
        className={classNames("flex", "items-end", "-mx-1", {
          "flex-row-reverse": reverse,
        })}
      >
        <div className="flex justify-center flex-shrink-0 px-1" ref={target}>
          <div
            className="overflow-hidden bg-gray-400 rounded-full"
            style={{
              width: DEFAULT_PROFILE_IMAGE_WIDTH,
              height: DEFAULT_PROFILE_IMAGE_HEIGHT,
            }}
          >
            {intersecting && (
              <button
                onClick={() =>
                  onProfileClick({
                    id: player.id,
                    name: calculateShortName(player.name),
                    games: GAME_IDS,
                    tournament: false,
                  })
                }
                className="block"
              >
                <ProfileImage
                  id={player.id}
                  name={calculateShortName(player.name)}
                />
              </button>
            )}
          </div>
        </div>
        <div className="w-2/3 px-1">
          <button
            onClick={() =>
              onProfileClick({
                id: player.id,
                name: calculateShortName(player.name),
                games: GAME_IDS,
                tournament: false,
              })
            }
            className={classNames(
              "w-full",
              "text-sm",
              "text-gray-600",
              "leading-none",
              "mb-1",
              { "text-left": !reverse, "text-right": reverse }
            )}
          >
            {calculateShortName(player.name)}
          </button>
          {[...messages].reverse().map((message, index) => (
            <GroupMessage
              id={message.id}
              message={message.message}
              reverse={reverse}
              isLast={index === messages.length - 1}
              key={index}
              currentChatId={currentChatId}
              isReported={message.isReported}
              setIsReported={setIsReported}
            />
          ))}
          <div
            className={classNames(
              "w-full",
              "text-xs",
              "text-gray-600",
              "leading-none",
              "mt-1",
              { "text-left": !reverse, "text-right": reverse }
            )}
          >
            {(() => {
              if (!messages || messages.length === 0) return null;

              const now = new Date().getTime();

              const lastMessage = messages.reduce((prev, curr) => {
                const currDate = new Date(curr.created);
                const prevDate = new Date(prev.created);

                const currTime = currDate.getTime();
                const prevTime = prevDate.getTime();

                if (isNaN(currTime)) return prev;
                if (isNaN(prevTime)) return curr;

                return Math.abs(currTime - now) < Math.abs(prevTime - now)
                  ? curr
                  : prev;
              });

              if (!lastMessage?.created) return null;

              const finalDate = new Date(lastMessage.created);

              if (isNaN(finalDate.getTime())) return null;

              return relativeTimeFormatter.format(finalDate, {
                addSuffix: true,
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export const Messages = React.memo<{
  container: React.MutableRefObject<HTMLDivElement | null>;
  onProfileClick: (profile: { id: string; name: string }) => void;
  currentUserId: string;
  messages: Array<ChatMessage>;
}>(({ container, messages, currentUserId, ...rest }) => {
  const [allMessages, setAllMessages] = React.useState<ChatMessage[]>([]);

  React.useEffect(() => {
    setAllMessages(messages);
  }, [messages]);

  const grouped = React.useMemo(() => {
    const grouped: Array<Array<ChatMessage>> = [];
    let currentId = null;
    let workingGroup: Array<ChatMessage> | null = null;
    for (const message of allMessages) {
      let newId = message.player.id;
      if (newId !== currentId) {
        currentId = newId;
        workingGroup = [];
        grouped.push(workingGroup);
      }
      workingGroup!.push(message);
    }
    return grouped;
  }, [allMessages]);

  const setIsReported = (messageId: string) => {
    const updatedMessages = messages.map((msg) =>
      msg.id === messageId ? { ...msg, isReported: true } : msg
    );

    setAllMessages(updatedMessages);
  };

  return (
    <>
      {grouped.map((group) => (
        <Group
          key={group[0].id}
          messages={group}
          container={container}
          currentChatId={currentUserId}
          setIsReported={setIsReported}
          {...rest}
        />
      ))}
    </>
  );
});
